import React from "react";

// Candidates named before the list collapses into "+N more". Matches the
// builder's in-use banner, which shows the same kind of list.
const NAMED_LIMIT = 3;

/**
 * The body of the delete-checklist dialog (Phase A; protected state ATH-I1765).
 *
 * Three states, chosen by the caller from the usage pre-flight:
 *
 * - **protected** — a country document set. Never deletable, whatever the
 *   counts say, so this is checked first. Points at the Active switch, which is
 *   what the officer actually wants when they stop recruiting in a country.
 * - **blocked** — candidates are assigned, so the delete cannot proceed *yet*.
 *   States the count, names who is still mid-process, and says what has to
 *   happen first.
 * - **free** — nothing is assigned, so it is an ordinary confirmation.
 *
 * The dialog hides its Delete button in the first two, so this copy is the whole
 * answer in both.
 *
 * Why protected outranks blocked: they are different answers. "3 candidates are
 * assigned" invites the officer to wait for those three to finish, and the
 * delete still fails afterwards. Only one of the two is permanent, and that is
 * the one worth saying.
 *
 * The old copy warned "Checklists assigned to a candidate cannot be deleted" on
 * *every* delete, which stated a rule without saying whether it applied to the
 * checklist in front of the officer. The count is what makes it an answer.
 */
const DeleteChecklistNotice = ({ checklistName, usage, loading, blocked, protectedMaster }) => {
  const name = <span className="font-bold text-text-primary break-words break-all">"{checklistName}"</span>;

  if (loading) {
    return <span className="text-text-secondary">Checking whether {name} is assigned to any candidate…</span>;
  }

  // Checked before `blocked`: a master with candidates assigned is both, and
  // the permanent refusal is the useful one.
  if (protectedMaster) {
    const country = usage?.countryCode;
    return (
      <span className="flex flex-col gap-2">
        <span className="text-text-primary">
          {name} is a <span className="font-semibold">country document set</span>
          {country ? ` for ${country}` : ""} — the starting point for every checklist your team builds for that country. It cannot be deleted.
        </span>
        <span className="text-text-secondary">
          To stop offering it, turn off <span className="font-medium text-text-primary">Active</span>. It disappears from the list of templates to start from, and every
          checklist already built from it is unaffected.
        </span>
        <span className="text-text-secondary">You can still add, edit and remove the documents inside it.</span>
      </span>
    );
  }

  if (!blocked) {
    return <span>{name} will be removed. Candidates can no longer be assigned to it, and it will stop appearing in the checklist dropdown.</span>;
  }

  const total = usage?.candidateCount ?? 0;
  const inFlight = usage?.inFlightCount ?? 0;
  // Only in-flight candidates are named: an approved candidate is not who the
  // officer is waiting on, and listing them all buries the ones that matter.
  const named = (usage?.candidates ?? [])
    .filter((c) => c.inFlight && c.candidateName)
    .slice(0, NAMED_LIMIT)
    .map((c) => c.candidateName);
  const overflow = Math.max(0, inFlight - named.length);

  return (
    <span className="flex flex-col gap-2">
      <span className="text-text-primary">
        <span className="font-semibold">
          {total} {total === 1 ? "candidate is" : "candidates are"} assigned
        </span>{" "}
        to {name}. It cannot be deleted until their compliance process is complete.
      </span>

      {inFlight > 0 && named.length > 0 && (
        <span className="text-text-secondary">
          Still mid-process: {named.join(", ")}
          {overflow > 0 ? ` +${overflow} more` : ""}.
        </span>
      )}

      {inFlight === 0 && total > 0 && (
        <span className="text-text-secondary">
          All of them have finished, but their records still reference this checklist — a document can arrive against it later, so it is
          kept.
        </span>
      )}

      <span className="text-text-secondary">
        To retire it from future use without deleting it, turn off <span className="font-medium text-text-primary">Active</span>. It then stops being offered for new
        candidates while everyone already assigned keeps theirs.
      </span>
    </span>
  );
};

export default DeleteChecklistNotice;
